/**
 * Pure helpers for the modern bike station popup.
 */

import batteryFull from "../../../image/icons/battery/batteryFull.svg";
import batteryHigh from "../../../image/icons/battery/batteryHigh.svg";
import batteryLow from "../../../image/icons/battery/batteryLow.svg";
import batteryMedium from "../../../image/icons/battery/batteryMedium.svg";
import bikeIcon from "../../../image/icons/map/transportIcons/BicycleIcon.svg";
import ebikeIcon from "../../../image/icons/map/transportIcons/EBicycleIcon.svg";
import type { BikeStationNameEntryDTO } from "../../../types/dtos/api/bike-station.dto";

const VEHICLE_TYPE_ICONS: Record<string, string> = {
    "bike":  bikeIcon,
    "ebike": ebikeIcon,
};

/**
 * Resolves the battery icon for a fuel percent in [0, 1].
 *
 * @param percent Fuel percent.
 * @returns SVG icon URL.
 */
export const getBatteryIcon = ( percent: number ): string => {
    if ( percent <= 0.25 ) {
        return batteryLow;
    }
    if ( percent <= 0.5 ) {
        return batteryMedium;
    }
    if ( percent <= 0.75 ) {
        return batteryHigh;
    }

    return batteryFull;
};

/**
 * Resolves the icon URL for a vehicle type id.
 *
 * @param vehicleTypeId Vehicle type id (e.g. "bike", "ebike").
 * @returns SVG icon URL or the raw id when no icon exists.
 */
export const getVehicleTypeIcon = ( vehicleTypeId: string ): string => VEHICLE_TYPE_ICONS[vehicleTypeId] ?? bikeIcon;

/**
 * Returns a human-readable label for a vehicle type id.
 *
 * @param vehicleTypeId Vehicle type id.
 * @returns French label.
 */
export const getVehicleTypeLabel = ( vehicleTypeId: string ): string => {
    if ( vehicleTypeId === "bike" ) {
        return "Vélo";
    } else if ( vehicleTypeId === "cargo" ) {
        return "Vélo cargo";
    } else if ( vehicleTypeId === "ebike" || ( /ebike|electric/i ).test( vehicleTypeId ) ) {
        return "Vélo électrique";
    }

    return "Vélo";
};

/**
 * Returns a human-readable label for a vehicle type id, pluralized when needed.
 *
 * @param vehicleTypeId Vehicle type id.
 * @param count Number of vehicles.
 * @returns French label in singular or plural form.
 */
export const getVehicleTypeLabelForCount = ( vehicleTypeId: string, count: number ): string => {
    if ( vehicleTypeId === "bike" ) {
        return count === 1 ? "Vélo" : "Vélos";
    } else if ( vehicleTypeId === "cargo" ) {
        return count === 1 ? "Vélo cargo" : "Vélos cargo";
    } else if ( vehicleTypeId === "ebike" || ( /ebike|electric/i ).test( vehicleTypeId ) ) {
        return count === 1 ? "Électrique" : "Électriques";
    }

    return count === 1 ? "Vélo" : "Vélos";
};

/**
 * Computes the fill ratio of a station given available bikes and capacity.
 *
 * @param bikesAvailable Number of bikes available.
 * @param capacity Station capacity.
 * @returns Ratio in [0, 1], or null when capacity is missing or zero.
 */
export const getCapacityFillRatio = (
    bikesAvailable: number | null | undefined,
    capacity: number | null | undefined,
): number | null => {
    if ( capacity == null || capacity === 0 || bikesAvailable == null ) {
        return null;
    }

    return Math.min( 1, Math.max( 0, bikesAvailable / capacity ) );
};

/**
 * Formats a range in meters to a human-readable string.
 *
 * @param meters Range in meters.
 * @returns Formatted string (e.g. "47.6 km" or "500 m").
 */
export const formatRange = ( meters: number | undefined ): string => {
    if ( meters == null ) {
        return "--";
    }

    if ( meters >= 1000 ) {
        return `${ ( meters / 1000 ).toFixed( 1 ) } km`;
    }

    return `${ Math.round( meters ) } m`;
};

/**
 * Formats a fuel percent in [0, 1] as a percentage string.
 *
 * @param percent Fuel percent.
 * @returns Percentage string or "--" when undefined.
 */
export const formatFuelPercent = ( percent: number | undefined ): string => {
    if ( percent == null ) {
        return "--";
    }

    return `${ Math.round( percent * 100 ) }%`;
};

/**
 * Resolves a bike station display name from the various shapes the backend
 * may return (string, single BikeStationNameEntryDTO, or array of entries).
 * Prefers the French entry, then the first available entry, and falls back
 * to an empty string when the input is unusable.
 *
 * @param name Raw name value from a backend DTO.
 * @returns Displayable string.
 */
export const resolveBikeStationDisplayName = ( name: unknown ): string => {
    if ( typeof name === "string" ) {
        return name;
    }

    if ( Array.isArray( name ) ) {
        const entries = name as Array<Pick<BikeStationNameEntryDTO, "language" | "text">>;
        const fr = entries.find( ( n ) => n.language === "fr" );

        return fr?.text ?? entries[0]?.text ?? "";
    }

    if ( name !== null && typeof name === "object" ) {
        const entry = name as BikeStationNameEntryDTO;

        return entry.text ?? "";
    }

    return "";
};
